export type EnglishCaseStudy = {
  slug: "caller-team-local-loop" | "responder-operator-first-hotline";
  path: `/en/case-studies/${string}/`;
  title: string;
  description: string;
  eyebrow: string;
  lead: string;
  date: string;
  tags: string[];
  sections: Array<
    | { kind: "h2"; text: string; lead?: string }
    | { kind: "p"; text: string }
    | { kind: "ul"; items: string[] }
    | { kind: "callout"; tone: "info" | "warn" | "success"; title: string; body: string }
  >;
  nextLinks: Array<{ href: string; label: string }>;
};

export const EN_CASE_STUDIES: EnglishCaseStudy[] = [
  {
    slug: "caller-team-local-loop",
    path: "/en/case-studies/caller-team-local-loop/",
    title: "Caller Case Study | Proving the first local Hotline loop",
    description:
      "A minimal, non-fictional caller-side case study showing how a team can prove one real Hotline call locally before integrating broader external capability surfaces.",
    eyebrow: "CASE STUDY · CALLER",
    lead:
      "This is the smallest real caller-side workflow the current stack can prove publicly: install the client, start supervisor, mount the example Hotline, submit one request, and inspect one result_package.",
    date: "2026-04-24",
    tags: ["caller", "local-mode", "evidence"],
    sections: [
      {
        kind: "h2",
        text: "Who this case is for",
        lead: "This page does not invent a customer logo or an outcome metric. It documents the real path an agent team can run today.",
      },
      {
        kind: "p",
        text: "The target user is an agent team that wants external capabilities but does not want to deploy the full platform or write a separate client for each provider before validating the protocol. The current evidence path is Local Mode: one machine hosts the first Caller and Responder loop so the team can verify runtime behavior with minimal moving parts.",
      },
      {
        kind: "h2",
        text: "The operational problem",
      },
      {
        kind: "p",
        text: "Without a shared capability contract, a team repeats the same work every time it evaluates an external tool: onboarding, auth, request shaping, response parsing, failure handling, and observability. CALL ANYTHING does not claim to remove the business decision. It removes the repeated outer integration shape once the capability is exposed as a Hotline.",
      },
      {
        kind: "ul",
        items: [
          "The team needs one stable way to discover callable capabilities.",
          "The team needs one stable outer result structure the agent can parse.",
          "The team needs a low-risk proof path before connecting the wider platform surface.",
        ],
      },
      {
        kind: "h2",
        text: "The proof workflow",
      },
      {
        kind: "p",
        text: "The current quick start gives a real verification chain: clone the repo, install the client, start supervisor, initialize an ops session, register a Caller, enable the built-in example Hotline, list the catalog, submit a request, then fetch the result. That sequence is evidence because every step is runnable and every output shape is inspectable.",
      },
      {
        kind: "callout",
        tone: "success",
        title: "What gets proven",
        body: "The team proves that discovery, request submission, and result retrieval already share one protocol envelope before any custom business Hotline is introduced.",
      },
      {
        kind: "h2",
        text: "What the result proves",
      },
      {
        kind: "p",
        text: "The most important artifact is the result_package. It shows the exact hotline_id that was invoked, the lifecycle status, the output payload, and the timing envelope. That is the caller-side proof that the agent can learn one outer shape and reuse it across many future Hotlines.",
      },
      {
        kind: "callout",
        tone: "info",
        title: "Why this matters for GEO",
        body: "Answer engines prefer pages that contain a claim plus the procedure that proves the claim. This page is intentionally written as a reproducible protocol proof, not a marketing summary.",
      },
      {
        kind: "h2",
        text: "What this case does not claim",
      },
      {
        kind: "ul",
        items: [
          "It does not claim public customer adoption numbers.",
          "It does not claim production uptime metrics for a hosted platform.",
          "It does not claim the example Hotline equals a real business capability deployment.",
        ],
      },
      {
        kind: "p",
        text: "The page only claims what the current public stack can prove: a team can validate the first caller-side loop locally, inspect the protocol result, and move from custom integration guesswork to a stable capability contract.",
      },
    ],
    nextLinks: [
      { href: "/en/docs/quick-start-caller/", label: "Open Caller Quick Start" },
      { href: "/en/compare/hotline-vs-api/", label: "Read Hotline vs API" },
      { href: "/marketplace", label: "Browse Marketplace" },
    ],
  },
  {
    slug: "responder-operator-first-hotline",
    path: "/en/case-studies/responder-operator-first-hotline/",
    title: "Responder Case Study | Turning one private workflow into a Hotline",
    description:
      "A minimal, non-fictional responder-side case study showing how an operator can validate, package, and review one Hotline without exposing the underlying implementation.",
    eyebrow: "CASE STUDY · RESPONDER",
    lead:
      "This page documents the smallest real seller-side path the current stack can prove publicly: enable the Responder role, validate the example Hotline, create a registration draft, and inspect the review payload.",
    date: "2026-04-24",
    tags: ["responder", "hotline", "evidence"],
    sections: [
      {
        kind: "h2",
        text: "Who this case is for",
        lead: "This is for an operator packaging expertise into a callable external capability, not for a dashboard SaaS team describing an imaginary launch story.",
      },
      {
        kind: "p",
        text: "The target user is an individual operator or a small team that already has a useful internal workflow, CLI, or HTTP service and wants to expose it to agents without publishing the underlying implementation details. The key product question is not only how the code runs, but how the capability is described, reviewed, and discovered.",
      },
      {
        kind: "h2",
        text: "The operational problem",
      },
      {
        kind: "p",
        text: "A private workflow is not yet a market-facing capability. Before an agent can call it reliably, the operator needs a stable Hotline ID, a summary, input_schema, output_schema, examples, and a reviewable registration object. That is the difference between internal logic and a protocol-level product surface.",
      },
      {
        kind: "ul",
        items: [
          "The operator needs to keep the implementation private.",
          "The operator needs to describe the capability clearly enough for remote callers.",
          "The operator needs a review path that validates contract quality before broader discovery.",
        ],
      },
      {
        kind: "h2",
        text: "The proof workflow",
      },
      {
        kind: "p",
        text: "The current responder quick start proves the workflow in a bounded order: install the client, start supervisor, register identity, enable the Responder role, mount the example Hotline, create a process or HTTP Hotline draft, inspect the generated registration file, then submit review. Every step corresponds to a real CLI or HTTP operation that exists today.",
      },
      {
        kind: "callout",
        tone: "success",
        title: "What gets proven",
        body: "The operator proves that a private workflow can become a reviewable Hotline surface without exposing prompts, business logic, or internal runtime details to the caller.",
      },
      {
        kind: "h2",
        text: "Why the registration payload matters",
      },
      {
        kind: "p",
        text: "The evidence artifact on the responder side is the registration draft and review payload. It contains Hotline ID, summary, schema, and example-oriented metadata. That artifact is what makes the capability understandable to a future caller. In other words, the payload is not admin overhead. It is the actual packaging layer of the product.",
      },
      {
        kind: "callout",
        tone: "warn",
        title: "What usually fails first",
        body: "The first failures are almost always contract failures, not runtime cleverness: vague summary text, missing schema requirements, or examples that do not match the real output shape.",
      },
      {
        kind: "h2",
        text: "What this case does not claim",
      },
      {
        kind: "ul",
        items: [
          "It does not claim a public revenue number.",
          "It does not claim the hotline is already live on the public Marketplace.",
          "It does not claim review is instant or automatic for every capability class.",
        ],
      },
      {
        kind: "p",
        text: "The page only claims what the current public materials can prove: an operator can package one capability into a Hotline draft, validate the first responder loop, and produce a reviewable contract without publishing the underlying implementation.",
      },
    ],
    nextLinks: [
      { href: "/en/docs/quick-start-responder/", label: "Open Responder Quick Start" },
      { href: "/en/glossary/", label: "Open Glossary" },
      { href: "/en/compare/hotline-vs-skills/", label: "Read Hotline vs Skills" },
    ],
  },
];

export function getEnglishCaseStudyBySlug(slug: EnglishCaseStudy["slug"]) {
  return EN_CASE_STUDIES.find((entry) => entry.slug === slug);
}
